import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroMovies } from '@/mocks/movies';

export default function HeroBanner() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroMovies.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroMovies.length) % heroMovies.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      window.REACT_APP_NAVIGATE(`/category/movie?search=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
    }
  };

  const currentMovie = heroMovies[currentSlide];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="精选推荐">
      {heroMovies.map((movie, idx) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={movie.coverImage}
            alt={movie.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 md:px-6">
        <div className="w-full max-w-3xl text-center">
          <h2 className="text-white text-2xl md:text-5xl font-heading font-bold mb-3 md:mb-4 drop-shadow-lg">
            {currentMovie.title}
          </h2>
          <p className="text-white/80 text-sm md:text-lg mb-4 md:mb-6 max-w-2xl mx-auto line-clamp-2 drop-shadow">
            {currentMovie.description}
          </p>

          <div className="flex items-center justify-center gap-3 mb-4 md:mb-6">
            <span className="px-3 py-1 text-xs bg-primary-500/90 text-white rounded-full font-medium">
              {currentMovie.rating} 分
            </span>
            <span className="text-white/70 text-xs">{currentMovie.year}</span>
            <span className="text-white/70 text-xs">{currentMovie.updateInfo}</span>
          </div>

          <Link
            to={`/article/${currentMovie.id}`}
            className="inline-flex items-center gap-2 px-6 py-2.5 md:px-8 md:py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-fill text-lg"></i>
            立即观看
          </Link>

          <form onSubmit={handleSearch} className="mt-6 md:mt-8 w-full max-w-lg mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="搜索你喜欢的电影、电视剧、综艺..."
                className="w-full pl-5 pr-12 py-3 md:py-3.5 text-sm bg-white/15 backdrop-blur-sm border border-white/20 rounded-full text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 transition-all duration-300"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-sm"></i>
              </button>
            </div>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              <span className="text-white/40 text-xs">热门搜索：</span>
              {['狂飙', '流浪地球3', '繁花', '海贼王'].map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => {
                    window.REACT_APP_NAVIGATE(`/category/movie?search=${encodeURIComponent(kw)}`);
                  }}
                  className="text-white/60 hover:text-white text-xs transition-colors cursor-pointer"
                >
                  {kw}
                </button>
              ))}
            </div>
          </form>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white transition-all cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white transition-all cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>

      <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
        {heroMovies.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              idx === currentSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`第${idx + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}